import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// --- external imports ------

import AuthLoadingScreen from '~/authentication/AuthLoadingScreen';
import { useUserContext } from '~/authentication/UserProvider';
import {
	NAME_MAX_LENGTH,
	EMAIL_MAX_LENGTH,
	PASSWORD_MAX_LENGTH,
	DEPARTMENT_OPTIONS
} from '~/authentication/lib/constants-auth';
import {
	validateName,
	validateEmail,
	validateNewPassword,
	validateConfirmPassword
} from '~/authentication/lib/credentialValidation';
import useAuthRequest from '~/authentication/lib/useAuthRequest';
import { isAbortedRequest } from '~/authentication/lib/classifyAuthError';
import { ERROR_CODES } from '~/authentication/lib/errors';
import { isSignInUnusable, markSignInUnusable } from '~/authentication/lib/unusableSignIn';
import InvalidLink from '~/authentication/InvalidLink';
import { LoadingCursor } from '~/containers/GameAsset/cursors';
// --- internal imports ------

import AuthBackdrop from '../AuthBackdrop';
import {
	ModalBackground,
	TitleText,
	FieldLabel,
	InputBackground,
	EyeButton,
} from '~/authentication/AuthAssets';
import {
	HalfFieldLabel,
	HalfInputBackground,
	HalfFieldInput,
	HalfErrorMessage,
	ErrorMessage,
	errorEmphasisClass,
	FieldInput,
	DropdownButton,
	DropdownMenuContainer,
	DropdownMenuItem,
	DropdownSelectedText,
	StartButton,
	StartButtonText,
	FormWrapper,
	TITLE_PROJECTION,
	FULL_INPUT_PROJECTION,
	HALF_INPUT_PROJECTION,
} from './RegisterPageAssets';
import { setupUserAccount } from './apis';
// --- sibling imports ------

const MENU_X = 926;
const MENU_Y = 676;

function RegisterPage() {
	const user = useUserContext();
	const navigate = useNavigate();
	// Set when Firebase refuses the password change because the sign-in behind this
	// form is too old to authorise one. There is no way back from it on this
	// screen: see the `canSetPassword` branch at the foot of this component.
	//
	// Seeded from storage rather than from `false`, because the session that
	// carries the player back here survives a reload and this has to survive with
	// it. Held in component state as well so the screen still turns over on the
	// refusal itself, in the mount that discovered it.
	const [hasUnusableSignIn, setHasUnusableSignIn] = useState(isSignInUnusable);
	const [firstNameError, setFirstNameError] = useState(null);
	const [lastNameError, setLastNameError] = useState(null);
	const [passwordError, setPasswordError] = useState(null);
	const [confirmPasswordError, setConfirmPasswordError] = useState(null);
	const [optionalEmailError, setOptionalEmailError] = useState(null);
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
	const [passwordValue, setPasswordValue] = useState('');
	const [confirmPasswordValue, setConfirmPasswordValue] = useState('');
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [selectedDepartment, setSelectedDepartment] = useState('');
	const [submitError, setSubmitError] = useState(null);

	const { isProcessing, runRequest } = useAuthRequest();

	async function handleOnSubmitForm(event) {
		event.preventDefault();

		// Cleared before the validation below can return early: the submit error and
		// the optional-email error share one slot on the form, so a failed submit
		// followed by an invalid retry would otherwise render both at once.
		setSubmitError(null);

		// Read through `elements` rather than off the form directly. A form does
		// expose its named controls as properties — `event.target.firstName` — but
		// that is the legacy named getter, and jsdom implements only the
		// `form.elements` half of it, so the screen could not be tested through its
		// own submit. Same values, same names, one that exists everywhere.
		const fields = event.target?.elements;
		const firstName = fields?.firstName?.value;
		const lastName = fields?.lastName?.value;
		const password = passwordValue;
		const confirmPassword = confirmPasswordValue;
		const optionalEmail = fields?.optionalEmail?.value;

		const { isValid: isValidFirstName, error: firstNameErr } = validateName(firstName);
		const { isValid: isValidLastName, error: lastNameErr } = validateName(lastName);
		const { isValid: isValidPassword, error: passwordErr } = validateNewPassword(password);
		const { isValid: isValidConfirmation, error: confirmationErr } =
			validateConfirmPassword(password, confirmPassword);
		const { isValid: isValidEmail, error: emailErr } = validateEmail(optionalEmail, false);

		setFirstNameError(firstNameErr);
		setLastNameError(lastNameErr);
		setPasswordError(passwordErr);
		setConfirmPasswordError(confirmationErr);
		setOptionalEmailError(emailErr);

		if (!user || !isValidFirstName || !isValidLastName || !isValidPassword ||
			!isValidConfirmation || !isValidEmail) {
			return;
		}

		try {
			await runRequest(async () => {
				// The password is set first, before anything reaches the backend.
				// `finishTradersUserRegistration` flips the licence from `pending` to
				// `active` in the same transaction that completes registration, and
				// that flip is not reversible from here — a later PATCH is refused
				// with `400 Player license is not valid`. So a password failure after
				// the PATCH left the player registered, without the password they
				// chose, and unable to retry. Failing here instead leaves the licence
				// pending and the form still usable.
				await user.firebaseUser.updatePassword(password);

				// Read after the password change: it invalidates the current token.
				const firebaseToken = await user.firebaseUser.getIdToken(true);
				const isAccountSetUp = await setupUserAccount(firebaseToken, {
					firstName,
					lastName,
					department: selectedDepartment,
					otherEmail: optionalEmail
				});

				// `setupUserAccount` reports a refusal as a falsy value rather than by
				// throwing. Without this the flow would fall straight through to the
				// sign-out below and hand the player a login form for an account that
				// was never set up.
				if (!isAccountSetUp) {
					throw new Error('The account could not be set up.');
				}

				// Registration ends the session on purpose: the password the player
				// just chose is the one they sign in with, so they are sent back to
				// the login form to use it.
				//
				// The registered row is deliberately not fetched or committed to the
				// context on the way. It carries `isRegistrationCompleted: true`, and
				// this screen is rendered by `GuardedRoute` under `/game/*` — so a
				// completed registration on the context is the guarded route's cue to
				// render `GameMounter`, which calls `launchGame()` from its render
				// body. One render between the two lines is enough to start the game
				// the player is being logged out of. The next session reads its own
				// user at login.
				await user.logOutPlayer();
				navigate('/login', { replace: true });
			});
		}
		catch (e) {
			console.error(e);

			// The request timed out rather than being refused, so it may well have
			// gone through. Asking the player to try again is the one thing that
			// cannot help: a second attempt after a PATCH that landed is rejected
			// outright, because the first one already activated the licence.
			//
			// Only the PATCH can reach here. It is the one call carrying an
			// `AbortSignal`; `updatePassword` and `getIdToken` fail with `auth/`
			// codes, which are not aborts. So by this point the password has
			// already been set — which is what makes "you are already registered"
			// true rather than half true. Keep this branch and the order above
			// together: moving the password back after the PATCH would strand a
			// timed-out player registered under a password they never chose.
			// Firebase guards `updatePassword` with a recency rule: it wants the
			// sign-in itself to be minutes old, not the token. Refreshing the token
			// does not help — `auth_time` is what it reads — so this is reached by a
			// player who simply spent long enough filling the form in, which on a
			// form with six fields and a dropdown is not an edge case at all.
			//
			// "Please try again" is the one thing that cannot work here: every retry
			// is made with a sign-in older than the last. Nor can the screen
			// re-authenticate on their behalf — the link that signed them in was
			// consumed on the way in, and the password that would replace it is the
			// one they are trying to set. A new link is the only way through, so the
			// screen that hands out new links is what they get.
			if (e?.code === ERROR_CODES.FIREBASE.GENERAL.REQUIRES_RECENT_LOGIN) {
				// Written down as well as set, so a reload does not hand the form back.
				// See `~/authentication/lib/unusableSignIn`.
				markSignInUnusable();
				setHasUnusableSignIn(true);
				return;
			}

			if (isAbortedRequest(e)) {
				setSubmitError('Your details were sent, but the server did not answer in time. \
Please reload this page before trying again — if it went through, you are already registered.');
				return;
			}

			setSubmitError('We could not complete your registration. Please try again.');
		}
	}

	const handleOnFirstNameBlur = useCallback((e) => {
		const { error } = validateName(e.target.value);
		setFirstNameError(error);
	}, []);

	const handleOnLastNameBlur = useCallback((e) => {
		const { error } = validateName(e.target.value);
		setLastNameError(error);
	}, []);

	const handleOnPasswordBlur = useCallback((e) => {
		const { error: pwdErr } = validateNewPassword(e.target.value);
		setPasswordError(pwdErr);
		const { error: confirmErr } = validateConfirmPassword(e.target.value, confirmPasswordValue);
		setConfirmPasswordError(confirmErr);
	}, [confirmPasswordValue]);

	const handleOnConfirmPasswordBlur = useCallback((e) => {
		const { error } = validateConfirmPassword(passwordValue, e.target.value);
		setConfirmPasswordError(error);
	}, [passwordValue]);

	const handleOnOptionalEmailBlur = useCallback((e) => {
		const { error } = validateEmail(e.target.value, false);
		setOptionalEmailError(error);
	}, []);

	const handlePasswordChange = useCallback((e) => {
		setPasswordValue(e.target.value);
	}, []);

	const handleConfirmPasswordChange = useCallback((e) => {
		setConfirmPasswordValue(e.target.value);
	}, []);

	const handleTogglePasswordVisibility = useCallback(() => {
		setIsPasswordVisible((prev) => !prev);
	}, []);

	const handleToggleConfirmPasswordVisibility = useCallback(() => {
		setIsConfirmPasswordVisible((prev) => !prev);
	}, []);

	const handleDropdownToggle = useCallback(() => {
		setIsDropdownOpen((prev) => !prev);
	}, []);

	const handleSelectDepartment = useCallback((option) => {
		setSelectedDepartment(option);
		setIsDropdownOpen(false);
	}, []);

	const shouldUsePasswordType = !isPasswordVisible || passwordValue.length === 0;
	const shouldUseConfirmPasswordType = !isConfirmPasswordVisible || confirmPasswordValue.length === 0;

	// The form is only worth showing to a player who can finish it, and finishing
	// it means `user.firebaseUser.updatePassword`. Two ways to arrive without one:
	//
	//   * Firebase refused the password change as too old a sign-in to authorise
	//     (`hasUnusableSignIn`, set in the submit handler above).
	//   * There is no Firebase session at all. `GuardedRoute` renders this screen
	//     off `tradersUserData.isRegistrationCompleted === false`, and that comes
	//     from the stored token, which is kept independently of the Firebase
	//     session and can outlive it. The registration link itself does not
	//     produce that state — `EmailLoginRedirector` never calls
	//     `setPersistence`, so a link sign-in is stored with the SDK default and
	//     survives a reload. The narrow door is a registration that set the
	//     password and then failed on the PATCH: that player now has a password
	//     and an incomplete registration, so they can use the login form, and
	//     `LoginPage` drops to NONE persistence when Remember Me is left
	//     unchecked. A reload then brings back the half that routes and loses the
	//     half that sets passwords. Unguarded, the submit read straight through
	//     `user.firebaseUser` and threw a TypeError that arrived as "We could not
	//     complete your registration. Please try again."
	//
	// Both end at the same place, because both need the same thing: a new link.
	// `InvalidLink` is the screen that hands them out, and it is rendered in place
	// rather than navigated to — it has no route of its own, which is also how
	// `EmailLoginRedirector` uses it. The session is deliberately left alone:
	// ending it would clear `tradersUserData`, and `GuardedRoute` would redirect
	// to /login on the next render, taking the explanation with it.
	//
	// The address is passed in rather than asked for. This player is already
	// known — it is their session that expired — so a field inviting them to type
	// one only offers the chance to type a different address and then wait for
	// mail that went somewhere else. It shows theirs, does not take input, and
	// leaves the button as the one thing to press.
	const knownEmail = user?.tradersUserData?.email ?? user?.firebaseUser?.email;

	// `GuardedRoute` holds the loading screen until the *user system* has
	// answered, which is a different question from whether Firebase has. Both
	// checks run on a cold load and neither waits for the other, so a stored
	// token that resolves first leaves a render with `tradersUserData` set and
	// `firebaseUser` still `undefined` — a session that is perfectly able to set
	// a password and has simply not reported yet. Read as an answer, that render
	// puts the recovery screen in front of a player who needs nothing, and a
	// press of its button sends them an email they never wanted.
	//
	// Telling the two apart by `null` versus `undefined` would not work:
	// `handleOnAuthStateChanged` returns early while a stored token is present,
	// before it sets the value, so a genuinely absent session leaves
	// `firebaseUser` `undefined` as well. The provider reports having been asked.
	if (!user?.hasSettledFirebaseAuth) {
		return <AuthLoadingScreen />;
	}

	const canSetPassword = Boolean(user?.firebaseUser) && !hasUnusableSignIn;

	if (!canSetPassword) {
		return (
			<InvalidLink
				title="LINK EXPIRED"
				lockedEmail={knownEmail}
				// The copy below promises a button-only flow, which is a promise
				// only a locked address can keep. With no address to show, the
				// field is back to being typed into, so the screen falls through
				// to the wording written for that — the title stays, because the
				// link really has expired either way.
				bodyText={knownEmail ? (
					<>
						Sorry, your sign-in expired before registration was finished.<br />
						Press the button below and we will send you a new link.
					</>
				) : undefined}
			/>
		);
	}

	return (
		<AuthBackdrop>
			{isProcessing && <LoadingCursor />}

			<ModalBackground
				x={528}
				y={148}
				shouldFadeIn={false}
			/>

			<TitleText
				x={550}
				y={73}
				projection={TITLE_PROJECTION}
			>
				REGISTRATION
			</TitleText>

			<FormWrapper onSubmit={handleOnSubmitForm}>
				<fieldset disabled={isProcessing}>

					{/* First Name */}
					<HalfFieldLabel
						x={644}
						y={177}
					>
						First Name
					</HalfFieldLabel>

					<HalfInputBackground
						x={644}
						y={203}
						projection={HALF_INPUT_PROJECTION}
					/>

					<HalfFieldInput
						id="firstName"
						name="firstName"
						x={644}
						y={203}
						projection={HALF_INPUT_PROJECTION}
						placeholder="First Name"
						type="text"
						maxLength={NAME_MAX_LENGTH}
						onBlur={handleOnFirstNameBlur}
						disabled={isProcessing}
						data-testid="register-first-name"
						hasError={!!firstNameError}
					/>

					{firstNameError && (
						<HalfErrorMessage
							x={644}
							y={261}
						>
							{firstNameError.normal}<span className={errorEmphasisClass}>{firstNameError.bold}</span>
						</HalfErrorMessage>
					)}

					{/* Last Name */}
					<HalfFieldLabel
						x={967}
						y={177}
					>
						Last Name
					</HalfFieldLabel>

					<HalfInputBackground
						x={967}
						y={203}
						projection={HALF_INPUT_PROJECTION}
					/>

					<HalfFieldInput
						id="lastName"
						name="lastName"
						x={967}
						y={203}
						projection={HALF_INPUT_PROJECTION}
						placeholder="Last Name"
						type="text"
						maxLength={NAME_MAX_LENGTH}
						onBlur={handleOnLastNameBlur}
						disabled={isProcessing}
						data-testid="register-last-name"
						hasError={!!lastNameError}
					/>

					{lastNameError && (
						<HalfErrorMessage
							x={967}
							y={261}
						>
							{lastNameError.normal}<span className={errorEmphasisClass}>{lastNameError.bold}</span>
						</HalfErrorMessage>
					)}

					{/* Email (read-only, from Firebase) */}
					<FieldLabel
						x={644}
						y={291}
						fontSize={16}
					>
						E-Mail
					</FieldLabel>

					<InputBackground
						x={644}
						y={317}
						projection={FULL_INPUT_PROJECTION}
					/>

					<FieldInput
						id="email"
						name="email"
						x={644}
						y={317}
						projection={FULL_INPUT_PROJECTION}
						placeholder="E-Mail"
						type="email"
						value={user?.firebaseUser?.email ?? ''}
						data-testid="register-email"
						disabled
					/>

					{/* Password */}
					<FieldLabel
						x={644}
						y={410}
						fontSize={16}
					>
						Password
					</FieldLabel>

					<InputBackground
						x={644}
						y={436}
						projection={FULL_INPUT_PROJECTION}
					/>

					<FieldInput
						id="password"
						name="password"
						x={644}
						y={436}
						projection={FULL_INPUT_PROJECTION}
						placeholder={'Password'}
						type={shouldUsePasswordType ? 'password' : 'text'}
						maxLength={PASSWORD_MAX_LENGTH}
						value={passwordValue}
						onChange={handlePasswordChange}
						onBlur={handleOnPasswordBlur}
						disabled={isProcessing}
						data-testid="register-password"
						hasError={!!passwordError}
					/>

					<EyeButton
						x={1226}
						y={453}
						isActive={isPasswordVisible}
						type="button"
						onClick={handleTogglePasswordVisibility}
						disabled={isProcessing}
						shouldFadeIn={false}
					/>

					{passwordError && (
						<ErrorMessage
							x={644}
							y={494}
						>
							{passwordError.normal}<span className={errorEmphasisClass}>{passwordError.bold}</span>
						</ErrorMessage>
					)}

					{/* Confirm Password */}
					<FieldLabel
						x={644}
						y={529}
						fontSize={16}
					>
						Confirm Password
					</FieldLabel>

					<InputBackground
						x={644}
						y={555}
						projection={FULL_INPUT_PROJECTION}
					/>

					<FieldInput
						id="confirmPassword"
						name="confirmPassword"
						x={644}
						y={555}
						projection={FULL_INPUT_PROJECTION}
						placeholder={'Confirm Password'}
						type={shouldUseConfirmPasswordType ? 'password' : 'text'}
						maxLength={PASSWORD_MAX_LENGTH}
						value={confirmPasswordValue}
						onChange={handleConfirmPasswordChange}
						onBlur={handleOnConfirmPasswordBlur}
						disabled={isProcessing}
						data-testid="register-password-confirm"
						hasError={!!confirmPasswordError}
					/>

					<EyeButton
						x={1226}
						y={572}
						isActive={isConfirmPasswordVisible}
						type="button"
						onClick={handleToggleConfirmPasswordVisibility}
						disabled={isProcessing}
						shouldFadeIn={false}
					/>

					{confirmPasswordError && (
						<ErrorMessage
							x={644}
							y={613}
						>
							{confirmPasswordError.normal}<span className={errorEmphasisClass}>{confirmPasswordError.bold}</span>
						</ErrorMessage>
					)}

					{/* Department dropdown */}
					<FieldLabel
						x={644}
						y={648}
						fontSize={16}
					>
						Department
					</FieldLabel>

					<DropdownButton
						x={644}
						y={674}
						type="button"
						onClick={handleDropdownToggle}
						disabled={isProcessing}
						shouldFadeIn={false}
						data-testid="register-department-dropdown"
					/>

					<DropdownSelectedText
						x={644}
						y={679}
						value={selectedDepartment}
						placeholder="Department"
						readOnly
					/>

					{isDropdownOpen && (
						<DropdownMenuContainer
							x={MENU_X}
							y={MENU_Y}
							shouldFadeIn={false}
						>
							{DEPARTMENT_OPTIONS.map((option, index) => {
								const isFirst = index === 0;
								const isLast = index === DEPARTMENT_OPTIONS.length - 1;
								return (
									<DropdownMenuItem
										key={option}
										isFirst={isFirst}
										isLast={isLast}
										isSelected={selectedDepartment === option}
										type="button"
										onClick={() => handleSelectDepartment(option)}
									>
										{option}
									</DropdownMenuItem>
								);
							})}
						</DropdownMenuContainer>
					)}

					{/* Other Email */}
					<FieldLabel
						x={644}
						y={767}
						fontSize={16}
					>
						Other Email (Optional)
					</FieldLabel>

					<InputBackground
						x={644}
						y={793}
						projection={FULL_INPUT_PROJECTION}
					/>

					<FieldInput
						id="optionalEmail"
						name="optionalEmail"
						x={644}
						y={793}
						projection={FULL_INPUT_PROJECTION}
						placeholder="Other E-Mail (Optional)"
						type="email"
						maxLength={EMAIL_MAX_LENGTH}
						onBlur={handleOnOptionalEmailBlur}
						disabled={isProcessing}
						data-testid="register-optional-email"
						hasError={!!optionalEmailError}
					/>

					{optionalEmailError && (
						<ErrorMessage
							x={644}
							y={851}
						>
							{optionalEmailError.normal}<span className={errorEmphasisClass}>{optionalEmailError.bold}</span>
						</ErrorMessage>
					)}

					{/* Shares the slot above: submitting requires every field to be valid,
						so a field error and a failed submission can never show together */}
					{submitError && (
						<ErrorMessage
							x={644}
							y={851}
						>
							{submitError}
						</ErrorMessage>
					)}

					{/* Submit */}
					{user && (
						<StartButton
							x={865}
							y={896}
							type="submit"
							disabled={isProcessing}
							shouldFadeIn={false}
							data-testid="register-submit"
						/>
					)}

					{user && (
						<StartButtonText
							x={888}
							y={907}
							shouldFadeIn={false}
							data-testid="register-submit-text"
						>
							Start Game!
						</StartButtonText>
					)}

				</fieldset>
			</FormWrapper>
		</AuthBackdrop>
	);
}

export default RegisterPage;
